<template>
  <div>
    <h1
      v-if="currentComponent !== 'ThankYou'"
      class="self-signup-v2-page-header"
      style="text-align: left;"
    >
      <i
        class="fas fa-arrow-circle-left fa-fw page-back-button"
        @click="handleBack({showWarning: true})"
      />
      {{ pageTitle }}
    </h1>
    <div class="self-signup-v2 new-enrol" style="min-height: 60%">
      <component
        :is="modalComponent"
        :timer="timer"
        :order="order"
        :active-participant="activeParticipant"
        @add-participant="addParticipant($event)"
        @modify-participant="modifyParticipant($event)"
        @cancel-booking="redirectToCalendar()"
        @restart-timer="restartTimer()"
      />
      <component
        :is="currentComponent"
        :course-id="course.id"
        :assets="assets"
        :payment-errors="paymentErrors"
        :order="order"
        :domain="domainDetails"
        :calendar-url="calendarUrl"
        :booking-type="order.bookingType"
        :booking-for-self="order.bookingForSelf"
        :error="formError"
        :course-type-id="course.unit_id"
        :preselected-contact="preselectedContact"
        :preselected-client="preselectedClient"
        :client-commenced="clientCommenced"
        :fees-calculation-path="feesCalculationPath"
        :available-payment-methods-path="availablePaymentMethodsPath"
        @halt-validation="haltValidation()"
        @apply-client-pricing="applyClientPricing($event)"
        @update-total="updateTotal($event)"
        @set-stripe-token="setStripeToken($event)"
        @update-vouchers="updateVouchers($event)"
        @finalize="finalizeOrder($event)"
        @change-page-title="changePageTitle($event)"
        @add-payment-method="addPaymentMethod($event)"
        @add-invoice-options="addInvoiceOptions($event)"
        @change-active-participant="changeActiveParticipant($event)"
        @assign-to-client="assignToClient($event)"
        @add-contact="addContact($event)"
        @add-participant="addParticipant($event)"
        @modify-participant="modifyParticipant($event)"
        @proceed="proceed($event)"
        @set-email="setEmail($event)"
        @remove-participant="removeParticipant($event)"
        @clear-errors="error = null"
        @raise-error="raiseError()"
        @show-modal="showModal($event)"
        @reserve-places="reservePlaces()"
        @include-self-in-booking="includeSelfInBooking($event)"
        @begin-timer="reservePlaces()"
        @set-places-required="setPlacesRequired($event)"
        @set-scope="setBookingType($event)"
      />
      <course-details
        v-if="currentComponent !== 'Payment' && currentComponent !== 'ThankYou'"
        :unit-constituents="course.course_constituents"
        :location="course.location"
        :venue-name="course.venue_name"
        :sessions="course.sessions"
        :unit-descriptor="course.unit_descriptor"
        :unit-subheading="course.unit_subheading"
        :booking-type="order.bookingType"
        :timezone-abbreviation="course.timezone_abbreviation"
        :unit-code="course.unit_code"
        :course-fee="course.fee"
        :course-fee-type="course.fee_type"
        :current-component="currentComponent"
      >
        <template #booking-type>
          <div
            v-if="showBookingType"
            class="tdesk-info-ultradark tdesk-meta-small"
            style="display:flex;align-items:baseline;margin-top:4px;"
          >
            <i
              class="fas fa-info-circle tdesk-info-ultradark fa-fw"
              style="font-size: 16px !important; display: block !important;"
            />
            <div class="course-detail-text small">
              {{ order.bookingType.charAt(0).toUpperCase() +
                order.bookingType.slice(1) }}
              booking
            </div>
          </div>
        </template>
        <template #timer>
          <vue-countdown
            v-if="timerExpiresAt"
            :time="timeRemaining"
            :emit-events="true"
            :transform="addLeadingZeroes"
            @progress="handleCountdownProgress"
            @end="redirectToCalendar()"
          >
            <template slot-scope="props">
              <div class="course-detail-item">
                <i class="tdesk-warn-ultradark fas fa-exclamation-triangle fa-fw" />
                <div class="course-detail-text small tdesk-warn-ultradark">
                  {{ order.placesRequired }} {{ pluralize(order.placesRequired,
                                                          'place') }}
                  reserved
                  for {{ props.minutes }}:{{
                    props.seconds }}
                </div>
              </div>
            </template>
          </vue-countdown>
        </template>
      </course-details>
    </div>
  </div>
</template>
<script>
  import CourseDetails from '../course_details';
  import ScopeSelection from './scope_selection';
  import SelfInclusionSelection from './self_inclusion_selection';
  import PlacesRequiredSelection from './places_required_selection';
  import EmailSelection from './email_selection';
  import ContactDetails from './contact_details';
  import Payment from './payment';
  import Confirmation from './confirmation';
  import CourseError from './course_error';
  import ExtendTimerModal from './extend_timer_modal';
  import ParticipantsList from './participants_list';
  import VueCountdown from '@chenfengyuan/vue-countdown';
  import AddParticipantModal from './add_participant_modal';
  import ThankYou from './thank_you';
  import EditParticipantModal from './edit_participant_modal';
  import ValidateNavigation from "../../../booking_portal/validate_navigation";
  import fireGaEvent from './fire_ga_event';
  import { NO_CLIENT_ID } from './constants';

  export default {
    components: {
      CourseDetails,
      ScopeSelection,
      SelfInclusionSelection,
      PlacesRequiredSelection,
      EmailSelection,
      VueCountdown,
      CourseError,
      Payment,
      Confirmation,
      ContactDetails,
      AddParticipantModal,
      EditParticipantModal,
      ParticipantsList,
      ThankYou,
      ExtendTimerModal,
    },
    props: {
      course: {
        type: Object,
        required: true,
      },
      assets: {
        required: true,
        type: Object,
      },
      domainDetails: {
        required: true,
        type: Object,
      },
      calendarUrl: {
        required: true,
        type: String,
      },
      preselectedContact: {
        required: false,
        type: Object,
        default: null,
      },
      preselectedClient: {
        required: false,
        type: Object,
        default: null,
      },
      feesCalculationPath: {
        required: false,
        type: String,
        default: 'calculate_total_enrols_path',
      },
      availablePaymentMethodsPath: {
        required: false,
        type: String,
        default: 'available_payment_methods_api_course_path',
      },
    },
    data() {
      return {
        order: {
          contact: null,
          reservationId: null,
          clientPricingApplied: null,
          bookingType: null,
          bookingForSelf: null,
          placesRequired: null,
          participants: [],
          appliedVouchers: [],
          paymentMethod: null,
          purchaseOrderNumber: null,
          contact_email: null,
          client: { id: NO_CLIENT_ID },
          course: this.course,
        },
        invoice: null,
        stripeTokenId: null,
        stripeToken: null,
        paymentErrors: [],
        activeParticipant: null,
        currentComponent: 'ScopeSelection',
        modalComponent: undefined,
        timerExpiresAt: null,
        timerRestarted: false,
        timeNow: null,
        timer: null,
        formError: null,
        error: null,
        showTimeRunningOutModal: false,
        pageTitle: 'Create a booking',
        interval: null,
      };
    },
    computed: {
      clientCommenced() {
        return !!this.preselectedClient;
      },
      timeRemaining() {
        return this.timerExpiresAt - this.timeNow;
      },
      showBookingType() {
        return !!this.order.bookingType;
      },
      nextStepComponent: function () {
        switch (this.currentComponent) {
          case "ScopeSelection":
            return 'SelfInclusionSelection';
          case "SelfInclusionSelection":
            if (this.order.bookingType === 'individual') {
              return 'EmailSelection';
            }
            return 'PlacesRequiredSelection';
          case "PlacesRequiredSelection":
            return "EmailSelection";
          case "EmailSelection":
            return "ContactDetails";
          case "ContactDetails":
            if (this.order.bookingForSelf && this.order.bookingType === 'individual') {
              return "Confirmation";
            }
            return "ParticipantsList";
          case 'ParticipantsList':
            return "Confirmation";
          case 'Confirmation':
            return "Payment";
        }

        return undefined;
      },
    },
    beforeMount() {
      this.checkCourseAvailability();
      window.addEventListener("beforeunload", this.preventNav);
    },
    beforeDestroy() {
      window.removeEventListener("beforeunload", this.preventNav);
      this.destroyReservation();
      this.haltValidation();
    },
    mounted() {
      fireGaEvent('ssbc_v2_course_selected', {'course_id': this.course.id, 'course_type': this.course.title});
      this.interval = setInterval(() => this.validateCourseStatus(), 30000);
    },
    methods: {
      haltValidation() {
        clearInterval(this.interval);
      },
      finalizeOrder() {
        // disable finalise-order-button in case of 0 fee
        $('button.finalise-order-button').prop('disabled', true);

        let data = {
          cid: this.order.course.id,
          prtc: JSON.stringify(this.order.participants),
          rvid: this.order.reservationId,
          clid: this.order.client.id,
          vids: this.order.appliedVouchers.map(voucher => voucher.id),
          pm: this.order.paymentMethod,
          bt: this.order.bookingType,
          ctc: JSON.stringify(this.order.contact),
          spc: this.stripeTokenId,
          cpa: this.order.clientPricingApplied,
          pon: this.order.purchaseOrderNumber,
          pdrid: this.domainDetails.public_directory_request_id,
          inv: this.invoice,
        };

        let url = Routes.finalize_order_enrols_path();

        axios
            .post(url, data)
            .then(rsp => {
              if (rsp.data.status === 'success') {
                fireGaEvent('ssbc_v2_success_booking', { 'payment_id': rsp.data.payment_id});

                if (rsp.data.redirect_url) {
                  window.removeEventListener("beforeunload", this.preventNav);
                  window.location.href = `https://${rsp.data.redirect_url}`;
                } else {
                  return this.proceed('ThankYou');
                }
              }
              this.paymentErrors = rsp.data.errors;
              $('#payment_form button').prop('disabled', false);
              $('button.finalise-order-button').prop('disabled', false);
            })
            .catch(err => {
              if (err.response.data.errors) {
                this.paymentErrors = err.response.data.errors;
              } else {
                this.paymentErrors = ['There was an error processing your order. Please check for any charges in your payment method and review your order history before placing the order again'];
              }
              this.$nextTick(() => {
                $('#payment_form button').prop('disabled', false);
                $('button.finalise-order-button').prop('disabled', false);
              });
            });
      },
      applyClientPricing(pricingApplied) {
        this.order.clientPricingApplied = pricingApplied;
      },
      updateTotal(total) {
        this.order.total = total;
      },
      updateVouchers(vouchers) {
        this.order.appliedVouchers = vouchers;
      },
      changePageTitle(title) {
        this.pageTitle = title;
      },
      changeActiveParticipant(participant) {
        this.activeParticipant = participant;
      },
      setStripeToken(tokenId) {
        this.stripeTokenId = tokenId;
      },
      assignToClient(client) {
        if (client) {
          this.order.client = {
            id: client.id,
            name: client.name,
          };
        } else {
          this.order.client = { id: NO_CLIENT_ID };
        }
      },
      addContact: function (contact) {
        this.order.contact = contact;
      },
      handleBack(options = {}) {
        if (this.currentComponent === 'ParticipantsList') {
          return this.currentComponent = 'ContactDetails';
        }
        if (this.currentComponent === 'Confirmation') {
          return this.currentComponent = 'ParticipantsList';
        }
        if (this.currentComponent === 'Payment') {
          return this.currentComponent = 'Confirmation';
        }
        this.redirectToCalendar(options);
      },
      addParticipant: function (participant) {
        this.order.participants.push(participant);
        toastr.success('Participant added successfully'); // eslint-disable-line
      },
      addPaymentMethod(method) {
        this.order.paymentMethod = method;
      },
      addInvoiceOptions(options) {
        this.invoice = options;
      },
      modifyParticipant: function (modifiedParticipant) {
        let x = this.order.participants.filter(participant => participant.id === modifiedParticipant.id)[0];
        this.removeParticipant(x);
        toastr.success('Participant edited successfully'); // eslint-disable-line
        this.order.participants.push(modifiedParticipant);
      },
      removeParticipant(participant) {
        var index = this.order.participants.indexOf(participant);
        if (index !== -1) {
          this.order.participants.splice(index, 1);
        }
      },
      preventNav(event) {
        if (this.error || !this.order.placesRequired || this.currentComponent === 'ThankYou') {
          return;
        }

        event.preventDefault();
        event.returnValue = "";
      },
      setBookingType: function (type) {
        this.order.bookingType = type;
      },
      setEmail: function (email) {
        this.order.contact_email = email;
      },
      validateCurrentStep() {
        let validation = new ValidateNavigation(this.order);
        return validation.validateCurrentStep(this.currentComponent);
      },
      proceed: function (forcedComponent = null) {
        if (this.error) {
          return;
        }
        if (!this.validateCurrentStep()) {return;}
        this.$nextTick(() => {
          if (forcedComponent) {
            return this.currentComponent = forcedComponent;
          }
          this.currentComponent = this.nextStepComponent;
        });
      },
      redirectToCalendar(options = {}) {
        if (!options.showWarning) {
          this.error = true;
        }
        window.location.replace(this.calendarUrl);
        toastr.warning('Your session has expired'); // eslint-disable-line
      },
      handleCountdownProgress(data) {
        this.timer = data;
        if (data.minutes === 0 && data.seconds === 59) {
          this.showModal('ExtendTimerModal');
        }
      },
      showModal(modalType) {
        this.modalComponent = modalType;
        if (modalType === 'AddParticipantModal') {
          return $('#add-participant-modal').modal('show');
        }
        if (modalType === 'EditParticipantModal') {
          return $('#edit-participant-modal').modal('show');
        }
      },
      getNow() {
        this.timeNow = new Date();
      },
      setPlacesRequired(numberRequired) {
        this.order.placesRequired = numberRequired;
      },
      includeSelfInBooking(decision) {
        this.order.bookingForSelf = decision;
      },
      pluralize(count, text) {
        if (count === 1) {
          return text;
        }
        return `${text}s`;
      },
      addLeadingZeroes(props) {
        Object.entries(props).forEach(([key, value]) => {
          // Adds leading zero
          const digits = value < 10 ? `0${value}` : value;
          props[key] = `${digits}`;
        });
        return props;
      },
      courseUpdatedSinceTimerBegan(timestamp) {
        const existingTimestamp = this.course.last_updated_at;
        return existingTimestamp !== timestamp;
      },
      validateCourseStatus() {
        axios
            .get(Routes.validate_course_status_enrols_path(this.course.id))
            .then(response => {
              const courseConfirmed = response.data.course_confirmed;
              const error = !courseConfirmed;
              if (error) {
                this.raiseError();
              }
            });
      },
      checkCourseAvailability() {
        axios
            .get(Routes.calculate_places_remaining_path(this.course.id))
            .then(response => {
              const courseNotConfirmed = response.data.course_confirmed === false;
              const placesRemaining = response.data.places_remaining;
              const notEnoughSpaces = this.order.placesRequired > placesRemaining;
              const error = courseNotConfirmed || notEnoughSpaces;
              if (error === false) {
                return;
              }
              this.raiseError();
            });
      },
      raiseError() {
        this.error = true;
        this.$nextTick(() => {
          this.redirectToError();
        });
      },
      redirectToError() {
        window.location.replace(Routes.course_unavailable_enrols_path());
      },
      restartTimer() {
        this.timerExpiresAt = null;
        this.timerRestarted = true;
        this.$nextTick(() => {
          this.extendTimer();
        });
      },
      extendTimer() {
        const data = {course_enrolment_reservation_id: this.order.reservationId};
        axios
            .put(Routes.course_enrolment_reservation_extend_timer_path(data))
            .then(response => {
              this.timerExpiresAt = new Date(response.data.timer_expires_at);
              if (!response.data.errors) {
                return;
              }
              this.raiseError();
            });
      },
      reservePlaces() {
        this.error = null;
        if (!this.timerRestarted) {
          this.checkCourseAvailability();
        }
        if (this.timerExpiresAt) {
          return;
        }
        let data = {
          session_unit_id: this.course.id,
          reserved_places: this.order.placesRequired,
        };
        const self = this;
        if (!this.error) {
          axios
              .post(Routes.course_enrolment_reservations_path(), data)
              .then((response) => {
                this.beginTimer(response.data.timer_expires_at);
                this.order.reservationId = response.data.id;
                if (self.order.bookingForSelf !== null) {
                  this.proceed();
                }
              }).catch(function (error) {
            self.formError = error.response.data.errors;
          });
        }
      },
      beginTimer(expiryTime) {
        this.getNow();
        this.timerExpiresAt = new Date(expiryTime);
      },
      destroyReservation() {
        if (!this.order.reservationId) {
          return;
        }
        axios.delete(Routes.course_enrolment_reservation_path({id: this.order.reservationId}));
      },
    },
  };
</script>
